import { useCallback, useEffect, useState } from 'react';
import axios from '../utils/axios';
import type { Application, Job } from '../types/portal';

export const PIPELINE_CHANGED = 'jobvictory:pipeline-changed';

export const workspaceApi = {
  async get<T>(url: string): Promise<T> {
    return (await axios.get<T>(url)).data;
  },
  async post<T>(url: string, data: unknown = {}): Promise<T> {
    return (await axios.post<T>(url, data, { preserveEmptyStrings: true })).data;
  },
  async put<T>(url: string, data: unknown = {}): Promise<T> {
    return (await axios.put<T>(url, data, { preserveEmptyStrings: true })).data;
  },
  async patch<T>(url: string, data: unknown): Promise<T> {
    return (await axios.patch<T>(url, data, { preserveEmptyStrings: true })).data;
  },
  async delete(url: string): Promise<void> {
    await axios.delete(url);
  },
  async download(url: string, filename: string) {
    const { data } = await axios.get<Blob>(url, { responseType: 'blob' });
    const href = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
  },
};

export function useWorkspaceData<T>(
  url: string,
  { backgroundRefresh = false }: { backgroundRefresh?: boolean } = {}
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((value) => value + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(!backgroundRefresh || version === 0);
    setError('');
    axios
      .get<T>(url, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setData(response.data);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err.message || 'Could not load this panel');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [url, version, backgroundRefresh]);
  return { data, setData, loading, error, reload };
}

export type Interview = {
  id: number;
  application_id: number;
  title: string;
  starts_at: string;
  ends_at: string;
  location: string | null;
  meeting_url: string | null;
  notes: string | null;
  status: 'scheduled' | 'completed' | 'cancelled';
  job_title: string;
  candidate_name: string;
  company_name: string;
};
export type InterviewInput = Pick<
  Interview,
  'application_id' | 'title' | 'starts_at' | 'ends_at' | 'location' | 'meeting_url' | 'notes'
>;
export type JobMatch = {
  job: Job;
  score: number;
  matched_skills: string[];
  reasons: string[];
  basis: string;
};
export type EmployeeInsights = {
  saved_jobs: number;
  profile_views: number;
  upcoming_interviews: Interview[];
  activity: { date: string; count: number }[];
};
export type Candidate = {
  id: number;
  name: string;
  headline: string | null;
  skills: string | null;
  location: string | null;
  experience_years: number | null;
  about: string | null;
  portfolio_url: string | null;
  has_applied: boolean;
};
export type Preferences = {
  discoverable: boolean;
  portfolio_url: string | null;
};
export type ResumeDraft = {
  full_name: string;
  email: string;
  phone: string;
  headline: string;
  location: string;
  summary: string;
  skills: string;
  experience: string;
  education: string;
  projects: string;
};
export type Company = {
  id: number;
  name: string;
  industry: string | null;
  location: string | null;
  about: string | null;
  website: string | null;
  open_jobs: number;
  jobs?: Job[];
};
export type SalaryData = {
  source: string;
  items: {
    category: string;
    count: number;
    minimum: number;
    maximum: number;
    average: number;
  }[];
};
export type HiringPlan = {
  id: number;
  name: string;
  description: string;
  price_inr: number;
  features: string[];
  active: boolean;
};
export type PlanRequest = {
  id: number;
  employer_id: number;
  employer_name: string;
  plan_id: number;
  plan_name: string;
  price_inr: number;
  status: 'pending' | 'approved' | 'rejected';
  note: string | null;
  created: string;
  reviewed_at: string | null;
};
export type BillingData = {
  plans: HiringPlan[];
  requests: PlanRequest[];
  current_plan: string;
  payment_mode: string;
};
export type ManagedAccount = {
  id: number;
  name: string;
  email: string;
  role: 'employee' | 'employer' | 'admin';
  company_name: string | null;
  created: string | null;
  suspended: boolean;
};
export type AdminOverview = {
  registered_users: number;
  active_employers: number;
  live_jobs: number;
  applications: number;
  revenue: number;
  revenue_note: string;
  pending_jobs: number;
  pending_plans: number;
  growth: { month: string; users: number; employers: number }[];
  recent_users: ManagedAccount[];
  jobs_by_category?: { name: string; count: number }[];
  applications_by_status?: { name: string; count: number }[];
  audit?: { id: number; action: string; target: string; created: string }[];
};
export type Category = {
  id: number;
  name: string;
  active: boolean;
  job_count: number;
};
export type PortalSettings = {
  site_name: string;
  support_email: string;
  require_job_approval: boolean;
};
export type Payment = {
  id: number;
  reference: string | null;
  amount: number;
  status: string;
  created: string | null;
  source: string;
};
export const PIPELINE_STAGES: {
  value: Application['pipeline_stage'];
  label: string;
  color: string;
}[] = [
  { value: 'new', label: 'New', color: '#4B8BEB' },
  { value: 'shortlisted', label: 'Shortlisted', color: '#8B73E8' },
  { value: 'interview', label: 'Interview', color: '#D39A4C' },
  { value: 'offer', label: 'Offer', color: '#35AFAB' },
  { value: 'hired', label: 'Hired', color: '#37B894' },
  { value: 'rejected', label: 'Not selected', color: '#CF8794' },
];
export const rupees = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
export const localDate = (value: string | null) =>
  value
    ? new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`).toLocaleDateString(
        'en-IN',
        {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }
      )
    : '—';
